import { isNotNil, isString } from "es-toolkit";
import { type RouteLocationRaw, useRoute, useRouter } from "vue-router";

export class RouteFacade {
    private route = useRoute();
    private router = useRouter();

    public get id() {
        const idParam = this.route.params.id;
        if (!isString(idParam)) throw new Error("id does not exist");
        return idParam;
    }

    public get optionalId() {
        const idParam = this.route.params.id;
        if (!isString(idParam)) return undefined;
        return idParam;
    }

    public get hasId() {
        return isString(this.route.params.id);
    }

    public param<T>(name: string) {
        const param = this.route.params[name];
        if (isString(param) && param.trim() === "") return undefined;
        return param as T | undefined;
    }

    public async push(to: RouteLocationRaw) {
        return await this.router.push(to);
    }

    public async replace(to: RouteLocationRaw) {
        return await this.router.replace(to);
    }

    public replaceFn(to: RouteLocationRaw) {
        return async () => {
            return await this.router.replace(to);
        };
    }

    public redirect(to: string) {
        if (import.meta.env.SSR) return;
        window.location.replace(to);
    }

    public redirectFn(to: string) {
        return async () => {
            if (import.meta.env.SSR) return;
            window.location.replace(to);
        };
    }

    public reload() {
        if (import.meta.env.SSR) return;
        window.location.reload();
    }

    public get back() {
        return () => {
            return this.router.back();
        };
    }

    public backTo(to: string) {
        return () => {
            if (isNotNil(this.router.options.history.state.back)) {
                return this.router.back();
            } else {
                return this.router.replace(to);
            }
        };
    }

    public get backRefresh() {
        return () => {
            if (isNotNil(this.router.options.history.state.back)) {
                this.router.back();
            } else {
                window.location.reload();
            }
        };
    }

    public openTab(uri: string) {
        window.open(uri, "_blank")?.focus();
    }
}
